import { useEffect, useId, useRef, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import styles from './ImageViewer.module.css';

export interface ImageViewerProps {
  src: string;
  /** Endereço para salvar o arquivo. */
  downloadHref: string;
  /** Descrição curta da imagem (quem mandou, nome do arquivo). */
  label: string;
  onClose: () => void;
}

const FOCUSABLE = 'a[href], button:not([disabled])';

/**
 * A imagem em tamanho grande, numa janela por cima da conversa. `Esc` e o clique fora fecham; o foco
 * fica preso dentro enquanto ela está aberta e volta ao botão que a abriu ao fechar.
 */
export function ImageViewer({ src, downloadHref, label, onClose }: ImageViewerProps): ReactElement {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    return () => opener?.focus?.({ preventScroll: true });
  }, []);

  // Teclado na janela inteira (não no elemento: ele não é interativo): Esc fecha e o Tab não sai dela.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) return;
      const active = document.activeElement;
      if (!dialog.current?.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  return createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <p id={titleId} className={styles.title}>
          {label}
        </p>
        <img className={styles.image} src={src} alt={label} />
        <div className={styles.actions}>
          <a className={styles.action} href={downloadHref}>
            Baixar
          </a>
          <button ref={closeButton} type="button" className={styles.action} onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
